import './styles/global.css';
import { initMap, mountLatLonGrid } from './map/setupMap';
import { mountHudHeader } from './map/hudHeader';
import { mountNewsPanel } from './map/newsPanel';
import { mountReconPanel } from './recon/reconPanel';
import { mountLayerChips } from './map/layerChips';
import { openEntityPopup, registerEntityPopupWithPanelStack } from './map/entityPopup';
import { LayerOrchestrator } from './map/LayerOrchestrator';
import { openSkySource } from './sources/flights';
import { usgsSource } from './sources/earthquakes';
import { createWindyWebcamsSource } from './sources/webcamsWindy';
import { createWsdotWebcamsSource } from './sources/webcamsWsdot';
import { wildfiresSource } from './sources/wildfires';
import { satellitesSource } from './sources/satellites';
import { maritimeSource } from './sources/maritime';
import { derSpCamerasSource } from './sources/webcamsDerSp';
import { newsSource } from './sources/news';
import type { DataSource, Entity } from './types/entity';

const mapRoot = document.getElementById('map-root');
const hudRoot = document.getElementById('hud-root');

if (!mapRoot || !hudRoot) {
  throw new Error('Containers #map-root / #hud-root ausentes no index.html');
}

const map = initMap(mapRoot);
mountLatLonGrid(hudRoot);
const headerStack = mountHudHeader(hudRoot);
mountNewsPanel(hudRoot);
mountReconPanel(hudRoot);

registerEntityPopupWithPanelStack();

function handleEntityClick(entity: Entity): void {
  /**
   * CORREÇÃO (Bug 4): antes, isto era um placeholder de
   * `console.info(...)` — não existia popup real. Sem um popup de
   * verdade, o cenário de teste pedido ("abrir popup de câmera, depois
   * tocar chip de camada") não era sequer reproduzível. Agora abre um
   * popup real via maplibregl.Popup, já integrado ao panelStack: abrir
   * este popup fecha qualquer bottom sheet competindo (news/recon), e
   * abrir um chip de camada (ver layerChips.ts) fecha este popup —
   * exclusividade nos dois sentidos, em qualquer breakpoint.
   */
  openEntityPopup(map, entity);
}

map.on('load', () => {
  const orchestrator = new LayerOrchestrator(map, handleEntityClick);

  // Fontes de camada de MAPA (têm marker/ping, controláveis por chip
  // de liga/desliga). 'news' fica de fora desta lista de propósito —
  // ver comentário abaixo de por que ela é tratada como panel chip,
  // não map-layer chip.
  const mapLayerSources: DataSource[] = [];

  function activateMapLayer(source: DataSource): void {
    orchestrator.activate(source);
    mapLayerSources.push(source);
  }

  // --- Camadas sem key, direto do client ---
  activateMapLayer(openSkySource);
  activateMapLayer(usgsSource);

  // --- Camadas com key simples, direto do client ---
  // Exceção documentada em app/.env.example: só Windy e WSDOT usam
  // prefixo VITE_. Ver memória de decisão do projeto.
  const windyKey = import.meta.env.VITE_WINDY_WEBCAMS_API_KEY as string | undefined;
  const wsdotCode = import.meta.env.VITE_WSDOT_ACCESS_CODE as string | undefined;

  if (windyKey) {
    activateMapLayer(createWindyWebcamsSource(windyKey));
  } else {
    console.warn('[main] VITE_WINDY_WEBCAMS_API_KEY ausente — camada Windy desativada.');
  }

  if (wsdotCode) {
    activateMapLayer(createWsdotWebcamsSource(wsdotCode));
  } else {
    console.warn('[main] VITE_WSDOT_ACCESS_CODE ausente — camada WSDOT desativada.');
  }

  // --- Camadas via Cloud Function (key sensível, nunca no client) ---
  activateMapLayer(wildfiresSource);
  activateMapLayer(satellitesSource);
  activateMapLayer(maritimeSource);

  // --- Dado estático pré-processado, servido via Cloud Function ---
  activateMapLayer(derSpCamerasSource);

  // --- Notícias (APITube, 12h de atraso no free tier) ---
  // Ativada como camada de mapa normalmente (busca automática,
  // artigos com coordenada viram marker estático — ver
  // LayerOrchestrator/EntityRenderer), mas NÃO entra na lista
  // mapLayerSources passada aos chips: no mobile, o chip de "news"
  // deve abrir a lista/bottom sheet (mountNewsPanel), não funcionar
  // como um simples toggle de liga/desliga de busca — ver
  // layerChips.ts, panelChips vs mapLayerSources.
  orchestrator.activate(newsSource);

  // ---- Chips de camada (mobile/tablet) ----
  // Implementa a especificação original nunca antes construída:
  // "camadas ativas em chips horizontais roláveis no topo".
  mountLayerChips(headerStack, orchestrator, mapLayerSources, [
    { panelId: 'news', label: 'Notícias' },
    { panelId: 'recon', label: 'RECON' },
  ]);
});
